import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { filter, take } from 'rxjs/operators';
import { PermissionService } from '../../services/permission.service';

/**
 * Smart landing-page redirect.
 *
 * Waits until permissions are loaded (reactive to the loaded$ stream),
 * then sends the user to the most relevant first page for their role:
 *
 *   CC Admin        → /daily-glance        (ops overview dashboard)
 *   Telemarketer    → /appointments-list   (marketing write access)
 *   Marketer        → /marketing/leads     (marketing read access)
 *   No role / empty → /unauthorised
 */
@Component({
  selector: 'app-home',
  standalone: true,
  template: `
    <div style="display:flex;align-items:center;justify-content:center;height:80vh;color:#6b7280;flex-direction:column;gap:0.75rem;">
      <i class="fas fa-circle-notch fa-spin" style="font-size:2rem;color:#16a34a;"></i>
      <span style="font-size:0.9rem;">Redirecting...</span>
    </div>
  `
})
export class HomeComponent implements OnInit {
  constructor(
    private router: Router,
    private permissionService: PermissionService
  ) { }

  ngOnInit(): void {
    // If permissions are already loaded (e.g. APP_INITIALIZER already ran), redirect immediately.
    if (this.permissionService.isPermissionsLoaded()) {
      this.redirect();
    } else {
      // Wait for the first emission of loaded$ = true before redirecting.
      this.permissionService.loaded$
        .pipe(
          filter(loaded => loaded),
          take(1)
        )
        .subscribe(() => this.redirect());
    }
  }

  private redirect(): void {
    if (this.permissionService.isCCAdmin()) {
      // CC Admins land on the ops overview dashboard first.
      this.router.navigate(['/daily-glance'], { replaceUrl: true });
    } else if (this.permissionService.isTelemarketer()) {
      // Telemarketers use appointments as their primary workflow.
      this.router.navigate(['/appointments-list'], { replaceUrl: true });
    } else if (this.permissionService.isMarketer()) {
      // Marketers go straight to Marketing Leads (read-only view).
      this.router.navigate(['/marketing/leads'], { replaceUrl: true });
    } else if (this.permissionService.isPermissionsLoaded()) {
      // Authenticated but no recognised role.
      this.router.navigate(['/unauthorised'], { replaceUrl: true });
    }
  }
}
